import { Link, useNavigate } from '@tanstack/react-router'
import { Check, Edit, MapPin, Store, Users } from 'lucide-react'
import { memo } from 'react'
import { CardActionsMenu } from '~/features/shared/components/card-actions-menu'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import type { Listing } from '~/shared/types/listing'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'

type RenderedListingCardProps = {
  listing: Listing
  isPendingPublish: boolean
  onPublish: (id: string, name: string, hasCoverImage: boolean) => void
}

export const RenderedListingCard = memo(function RenderedListingCard({
  listing,
  isPendingPublish,
  onPublish,
}: RenderedListingCardProps) {
  const navigate = useNavigate()
  const isDraft = listing.isDraft || listing.status === 'Draft'

  return (
    <Card className="beacon-interactive group relative flex flex-col overflow-hidden">
      {/* Whole-card click opens the place detail; the options menu and the publish
			    button sit above this overlay (z-20) so they keep their own handlers. */}
      <Link
        to="/dashboard/listings/$listingId"
        params={{ listingId: listing.id }}
        aria-label={listing.name}
        className="absolute inset-0 z-10"
      />
      <div className="absolute top-2 right-2 z-20">
        <CardActionsMenu
          label={m.cardOptions()}
          actions={[
            {
              label: m.edit(),
              icon: Edit,
              onSelect: () =>
                navigate({
                  to: '/dashboard/listings/$listingId/edit',
                  params: { listingId: listing.id },
                }),
            },
          ]}
        />
      </div>
      {listing.coverImageUrl ? (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={listing.coverImageUrl}
            alt={listing.name}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="aspect-video w-full bg-muted/50 flex items-center justify-center">
          <Store className="h-12 w-12 text-muted-foreground/40" />
        </div>
      )}

      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="line-clamp-2 text-brand-subtle-foreground">
            {listing.name}
          </CardTitle>
          <div className="flex gap-1">
            {isDraft && <Badge variant="secondary">{m.draft()}</Badge>}
          </div>
        </div>
        <CardDescription className="line-clamp-2">{listing.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="line-clamp-1">
                {listing.location.city}, {countryDisplayName(listing.location.country)}
              </span>
            </div>
            <div
              className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground"
              title={m.follower()}
            >
              <Users className="h-4 w-4" aria-hidden="true" />
              <span className="tabular-nums">{listing.interestedCount.toLocaleString()}</span>
              <span className="sr-only">{m.follower()}</span>
            </div>
          </div>
        </div>

        {isDraft && (
          <div className="relative z-20 pt-4 mt-auto">
            {listing.coverImageUrl ? (
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => onPublish(listing.id, listing.name, !!listing.coverImageUrl)}
                disabled={isPendingPublish}
              >
                <Check className="mr-2 h-4 w-4" />
                {m.publish()}
              </Button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/dashboard/listings/$listingId/edit"
                      params={{ listingId: listing.id }}
                      search={{ scrollTo: 'images' }}
                      className="block"
                    >
                      <Button variant="default" size="sm" className="w-full" disabled>
                        <Check className="mr-2 h-4 w-4" />
                        {m.publish()}
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{m.addACoverImageToPublish1()}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
})
